import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, CheckCircle2, Clock, Plus, X } from 'lucide-react';
import type {
  AssetStatusValue,
  Handover,
  HandoverItem,
  SaveHandoverRequest,
} from '@up2d8/contracts';
import { HANDOVER_MAX_PHOTOS } from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import {
  AppSubBar,
  Badge,
  Button,
  Dialog,
  EmptyState,
  Input,
  Textarea,
  cn,
  useToast,
} from '@up2d8/ui';
import { useOffline } from '../../../offline/use-offline';
import { useUpload } from '../../../shared/use-upload';
import { renderAppTabLink, useAppTabs } from '../app-tabs';
import { DeskShell } from '../desk/desk-shell';
import { MessagesIcon } from '../messages-icon';
import { NotificationBell } from '../notification-bell';
import {
  HandoverCard,
  HandoverPhoto,
  OPEN_CLOSE_SELECTED,
  Segments,
  StatusIcon,
  statusOptions,
} from './handover-ui';
import {
  clock,
  shortDate,
  storeDraftLocally,
  useHandoverDraft,
  useHandoverHistory,
  useSaveDraft,
  useSubmitHandover,
  weekdayShort,
} from './use-handover';

/** De vorm van het concept in het geheugen van de app: wat de server terugkrijgt, plus wat erbij hoort. */
interface DraftState {
  notes: string;
  assets: { assetId: string; name: string; status: AssetStatusValue; note: string }[];
  items: {
    id: string;
    text: string;
    status: 'open' | 'closed';
    closeNote: string;
    carried: boolean;
    openedAt: string;
    openedByName: string;
    fresh: boolean;
  }[];
  fileIds: string[];
}

function fromHandover(handover: Handover): DraftState {
  return {
    notes: handover.notes,
    assets: handover.assets.map(({ assetId, name, status, note }) => ({
      assetId,
      name,
      status,
      note,
    })),
    items: handover.items.map((item: HandoverItem) => ({
      id: item.id,
      text: item.text,
      status: item.status,
      closeNote: item.closeNote,
      carried: item.carried,
      openedAt: item.openedAt,
      openedByName: item.openedByName,
      // Een eigen punt mag je bijschrijven; een doorgeschoven punt is van iemand anders.
      fresh: !item.carried,
    })),
    fileIds: handover.fileIds,
  };
}

/** Het concept zoals het lokaal bewaard wordt: dezelfde vorm als van de server. */
function toLocalHandover(server: Handover, state: DraftState): Handover {
  return {
    ...server,
    notes: state.notes,
    fileIds: state.fileIds,
    assets: state.assets.map(({ assetId, name, status, note }) => ({
      assetId,
      name,
      status,
      note,
    })),
    items: state.items.map((item) => ({
      id: item.id,
      text: item.text,
      status: item.status,
      openedById: server.authorId,
      openedByName: item.openedByName,
      openedAt: item.openedAt,
      carried: item.carried,
      closedByName: null,
      closedAt: null,
      closeNote: item.closeNote,
    })),
  };
}

function toRequest(state: DraftState, takeOver = false): SaveHandoverRequest {
  return {
    notes: state.notes,
    assets: state.assets.map(({ assetId, status, note }) => ({ assetId, status, note })),
    // Een nieuw punt zonder tekst gaat nog niet mee: de server weigert een leeg punt, en een
    // autosave die faalt op een half getypte regel zou het hele concept tegenhouden.
    items: state.items
      .filter((item) => item.text.trim().length > 0)
      .map(({ id, text, status, closeNote }) => ({ id, text: text.trim(), status, closeNote })),
    fileIds: state.fileIds,
    takeOver,
  };
}

/**
 * De overdracht schrijven (Stap 39, OVD-2, spec §3 frames 3, 3b en 4, en §4 "Schrijven").
 *
 * **Autosave**, anderhalve seconde na de laatste wijziging; "Concept bewaard om 21:46" komt van
 * de server (`updatedAt`). Offline blijft het concept lokaal staan en is indienen uitgeschakeld
 * (spec §5).
 *
 * **Eén schrijver tegelijk** (besluit van 7 oktober 2026): is een collega bezig, dan staat dat
 * bovenaan met "Neem over", en is het formulier alleen-lezen tot je dat doet.
 */
export function AppHandoverWritePage() {
  const { lineId, slotId } = useParams({ strict: false }) as { lineId: string; slotId: string };
  const { t, i18n } = useTranslation();
  const tabs = useAppTabs();
  const navigate = useNavigate();
  const toast = useToast();
  const { online } = useOffline();

  const draft = useHandoverDraft(lineId, slotId);
  const save = useSaveDraft(lineId, slotId);
  const submit = useSubmitHandover();
  const upload = useUpload();

  const [server, setServer] = useState<Handover | null>(null);
  const [state, setState] = useState<DraftState | null>(null);
  const [dirty, setDirty] = useState(false);
  const [controle, setControle] = useState(false);
  const [ingediend, setIngediend] = useState<Handover | null>(null);
  const [alleMachines, setAlleMachines] = useState(false);
  const [fouten, setFouten] = useState<Set<string>>(new Set());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const data = draft.data?.data;
    if (!data || server) return;
    setServer(data);
    setState(fromHandover(data));
  }, [draft.data, server]);

  const mine = server?.isMine ?? true;
  const locked = server?.status === 'locked';
  const readOnly = !mine || locked || !(server?.editable ?? true);

  const persist = async (next: DraftState, takeOver = false) => {
    if (!server) return;
    // Lokaal altijd, zodat een concept een verloren verbinding overleeft (spec §5).
    await storeDraftLocally(lineId, slotId, toLocalHandover(server, next));
    if (!online) return;
    try {
      const saved = await save.mutateAsync(toRequest(next, takeOver));
      setServer(saved);
      await storeDraftLocally(lineId, slotId, saved);
      setDirty(false);
    } catch (error) {
      toast.show({
        tone: 'danger',
        title: t('handover.saveFailed'),
        description: problemDetail(error) ?? undefined,
      });
    }
  };

  const change = (update: (current: DraftState) => DraftState) => {
    if (!state || readOnly) return;
    const next = update(state);
    setState(next);
    setDirty(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void persist(next), 1500);
  };

  // Weer online: wat lokaal bleef staan, gaat alsnog mee.
  useEffect(() => {
    if (online && dirty && state) void persist(state);
  }, [online]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const samenvatting = useMemo(() => {
    if (!state) return null;
    const open = state.items.filter((item) => item.status === 'open' && item.text.trim());
    return {
      stopped: state.assets.filter((asset) => asset.status === 'stopped').length,
      fault: state.assets.filter((asset) => asset.status === 'fault').length,
      open: open.length,
      fresh: open.filter((item) => !item.carried).length,
      closed: state.items.filter((item) => item.status === 'closed' && item.carried).length,
      photos: state.fileIds.length,
    };
  }, [state]);

  const volgende = server?.next ?? null;
  const resterend = volgende
    ? Math.round((new Date(volgende.startsAt).getTime() - Date.now()) / 60000)
    : null;

  const controleer = () => {
    if (!state) return;
    const zonder = new Set(
      state.assets
        .filter((asset) => asset.status !== 'running' && !asset.note.trim())
        .map((asset) => asset.assetId),
    );
    setFouten(zonder);
    if (zonder.size > 0) {
      toast.show({ tone: 'danger', title: t('handover.noteRequired') });
      return;
    }
    setControle(true);
  };

  const dienIn = async () => {
    if (!server || !state) return;
    try {
      if (timer.current) clearTimeout(timer.current);
      const saved = await save.mutateAsync(toRequest(state));
      const done = saved.status === 'draft' ? await submit.mutateAsync(saved.id) : saved;
      setControle(false);
      setIngediend(done);
    } catch (error) {
      toast.show({
        tone: 'danger',
        title: t('handover.submitFailed'),
        description: problemDetail(error) ?? undefined,
      });
    }
  };

  const shell = (children: ReactNode) => (
    <DeskShell
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="handover"
      renderLink={renderAppTabLink}
    >
      <AppSubBar
        title={t('handover.writeTitle')}
        onBack={() => void navigate({ to: '/app/overdracht' })}
      />
      {children}
    </DeskShell>
  );

  // --- Frame 4: ingediend -------------------------------------------------------------------
  if (ingediend) {
    return shell(
      <HandoverCard data-testid="handover-submitted">
        <div className="flex flex-col items-center gap-element py-6 text-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-success-tint text-success-tint-text">
            <CheckCircle2 aria-hidden className="h-9 w-9" />
          </span>
          <h1 className="font-heading text-[22px] font-semibold text-text-primary">
            {t('handover.submittedTitle')}
          </h1>
          {ingediend.next ? (
            <p className="max-w-sm text-content text-text-secondary">
              {t('handover.submittedHint', {
                slot: ingediend.next.slotName,
                time: clock(ingediend.next.startsAt, i18n.language),
              })}
            </p>
          ) : null}
          <div className="flex w-full max-w-sm flex-col gap-2">
            <Button
              size="lg"
              data-testid="handover-view"
              onClick={() =>
                void navigate({
                  to: '/app/overdracht/$handoverId',
                  params: { handoverId: ingediend.id },
                })
              }
            >
              {t('handover.view')}
            </Button>
            <Button size="lg" variant="secondary" onClick={() => void navigate({ to: '/app' })}>
              {t('handover.toHome')}
            </Button>
          </div>
        </div>
      </HandoverCard>,
    );
  }

  // Op `state` en niet op `draft.isPending`: offline wisselt de sleutel van het concept, en dan mag
  // het formulier dat al op het scherm staat niet wijken voor "Bezig met laden…" (#4).
  if (!state || !server) {
    return shell(
      draft.isError || (draft.data && !draft.data.data) ? (
        <HandoverCard>
          <EmptyState
            title={t('handover.draftUnavailable')}
            description={t('handover.draftUnavailableHint')}
          />
        </HandoverCard>
      ) : (
        <HandoverCard>
          <p className="text-content text-text-secondary">{t('common.loading')}</p>
        </HandoverCard>
      ),
    );
  }

  const summary = samenvatting ? (
    <ul
      className="flex flex-col gap-2 text-content text-text-primary"
      data-testid="handover-summary"
    >
      {samenvatting.stopped > 0 ? (
        <li className="flex items-center gap-2 text-danger-tint-text">
          <StatusIcon status="stopped" size={18} />
          {t('handover.sumStopped', { count: samenvatting.stopped })}
        </li>
      ) : null}
      {samenvatting.fault > 0 ? (
        <li className="flex items-center gap-2 text-[var(--orange-tint-text-aa)]">
          <StatusIcon status="fault" size={18} />
          {t('handover.sumFault', { count: samenvatting.fault })}
        </li>
      ) : null}
      <li>{t('handover.sumOpen', { count: samenvatting.open, fresh: samenvatting.fresh })}</li>
      {samenvatting.closed > 0 ? (
        <li>{t('handover.sumClosed', { count: samenvatting.closed })}</li>
      ) : null}
      {samenvatting.photos > 0 ? (
        <li>{t('handover.sumPhotos', { count: samenvatting.photos })}</li>
      ) : null}
    </ul>
  ) : null;

  const indienKnop = (
    <Button
      size="lg"
      className="w-full"
      disabled={!online || readOnly}
      onClick={controleer}
      data-testid="handover-check"
    >
      {online ? t('handover.checkAndSubmit') : t('handover.offlineSubmit')}
    </Button>
  );

  return shell(
    <div
      className="flex flex-col gap-element pb-28 wide:grid wide:grid-cols-[minmax(0,1fr)_320px] wide:items-start wide:gap-5 wide:pb-0"
      data-testid="handover-write"
    >
      <div className="flex min-w-0 flex-col gap-element">
        {/* Frame 3: oranje uitlegvlak met klok, of de melding dat een collega bezig is. */}
        {!mine ? (
          <div
            className="flex flex-wrap items-center gap-3 rounded-md bg-brand-orange-tint p-3.5 text-content text-text-primary"
            data-testid="handover-busy"
          >
            <span className="flex-1">{t('handover.busy', { name: server.authorName })}</span>
            <Button
              size="sm"
              variant="urgent"
              data-testid="handover-take-over"
              disabled={!online}
              onClick={() => void persist(state, true)}
            >
              {t('handover.takeOver')}
            </Button>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-md bg-brand-orange-tint p-3.5 text-small text-text-primary">
            <Clock
              aria-hidden
              className="mt-0.5 h-5 w-5 shrink-0 text-[var(--orange-tint-text-aa)]"
            />
            <span data-testid="handover-saved">
              {server.lineName} · {server.slotName}
              {resterend !== null && resterend > 0 && resterend < 240
                ? ` · ${t('handover.minutesLeft', { count: resterend })}`
                : ''}
              {'. '}
              {save.isPending
                ? t('handover.saving')
                : t('handover.savedAt', { time: clock(server.updatedAt, i18n.language) })}
            </span>
          </div>
        )}

        {locked ? <Badge tone="neutral">{t('handover.locked')}</Badge> : null}

        {state.assets.length > 0 ? (
          <section className="flex flex-col gap-2.5" data-testid="handover-write-machines">
            <h2 className="font-heading text-[17px] font-semibold text-text-primary">
              {t('handover.machines')}
            </h2>
            {state.assets
              .filter((asset) => alleMachines || asset.status !== 'running')
              .map((asset) => (
                <HandoverCard key={asset.assetId} data-testid="handover-write-machine">
                  <span className="font-heading text-content font-semibold text-text-primary">
                    {asset.name}
                  </span>
                  <Segments
                    label={asset.name}
                    value={asset.status}
                    disabled={readOnly}
                    options={statusOptions(t)}
                    testId={`status-${asset.name}`}
                    onChange={(status) =>
                      change((current) => ({
                        ...current,
                        assets: current.assets.map((candidate) =>
                          candidate.assetId === asset.assetId
                            ? { ...candidate, status }
                            : candidate,
                        ),
                      }))
                    }
                  />
                  {asset.status !== 'running' ? (
                    <Input
                      label={t('handover.machineNote')}
                      value={asset.note}
                      maxLength={500}
                      disabled={readOnly}
                      error={fouten.has(asset.assetId) ? t('handover.noteRequired') : undefined}
                      onChange={(event) => {
                        const note = event.target.value;
                        change((current) => ({
                          ...current,
                          assets: current.assets.map((candidate) =>
                            candidate.assetId === asset.assetId
                              ? { ...candidate, note }
                              : candidate,
                          ),
                        }));
                      }}
                    />
                  ) : null}
                </HandoverCard>
              ))}
            {!alleMachines && state.assets.some((asset) => asset.status === 'running') ? (
              <Button
                variant="ghost"
                onClick={() => setAlleMachines(true)}
                data-testid="handover-show-running"
              >
                {t('handover.showRunning', {
                  count: state.assets.filter((asset) => asset.status === 'running').length,
                })}
              </Button>
            ) : null}
          </section>
        ) : null}

        <section className="flex flex-col gap-2.5" data-testid="handover-write-items">
          <h2 className="font-heading text-[17px] font-semibold text-text-primary">
            {t('handover.openItems')}
          </h2>
          {state.items.map((item) => (
            <HandoverCard key={item.id} data-testid="handover-write-item">
              {item.carried ? (
                <>
                  <p className="font-heading text-content font-semibold text-text-primary">
                    {item.text}
                  </p>
                  <Badge tone="orange" className="self-start">
                    {t('handover.carriedSince', {
                      day: weekdayShort(item.openedAt, i18n.language),
                    })}
                  </Badge>
                </>
              ) : (
                <>
                  <Input
                    label={t('handover.itemText')}
                    value={item.text}
                    maxLength={500}
                    disabled={readOnly || !item.fresh}
                    data-testid="handover-item-text"
                    onChange={(event) => {
                      const text = event.target.value;
                      change((current) => ({
                        ...current,
                        items: current.items.map((candidate) =>
                          candidate.id === item.id ? { ...candidate, text } : candidate,
                        ),
                      }));
                    }}
                  />
                  <Badge tone="primary" className="self-start">
                    {t('handover.new')}
                  </Badge>
                </>
              )}
              <Segments
                label={item.text || t('handover.itemText')}
                value={item.status}
                disabled={readOnly}
                testId="item-choice"
                options={[
                  {
                    value: 'open' as const,
                    label: t('handover.stillOpen'),
                    selectedClass: OPEN_CLOSE_SELECTED,
                  },
                  {
                    value: 'closed' as const,
                    label: t('handover.close'),
                    selectedClass: OPEN_CLOSE_SELECTED,
                  },
                ]}
                onChange={(status) =>
                  change((current) => ({
                    ...current,
                    items: current.items.map((candidate) =>
                      candidate.id === item.id ? { ...candidate, status } : candidate,
                    ),
                  }))
                }
              />
              {item.status === 'closed' ? (
                <Input
                  label={t('handover.closeNote')}
                  value={item.closeNote}
                  maxLength={500}
                  disabled={readOnly}
                  onChange={(event) => {
                    const closeNote = event.target.value;
                    change((current) => ({
                      ...current,
                      items: current.items.map((candidate) =>
                        candidate.id === item.id ? { ...candidate, closeNote } : candidate,
                      ),
                    }));
                  }}
                />
              ) : null}
            </HandoverCard>
          ))}
          <Button
            variant="secondary"
            disabled={readOnly}
            data-testid="handover-add-item"
            onClick={() =>
              change((current) => ({
                ...current,
                items: [
                  ...current.items,
                  {
                    id: crypto.randomUUID(),
                    text: '',
                    status: 'open',
                    closeNote: '',
                    carried: false,
                    openedAt: new Date().toISOString(),
                    openedByName: '',
                    fresh: true,
                  },
                ],
              }))
            }
          >
            <Plus aria-hidden className="mr-2 h-4 w-4" />
            {t('handover.addItem')}
          </Button>
        </section>
      </div>

      <div className="flex flex-col gap-element">
        <HandoverCard>
          <Textarea
            label={t('handover.notes')}
            value={state.notes}
            maxLength={4000}
            disabled={readOnly}
            className="min-h-[88px]"
            data-testid="handover-notes-input"
            onChange={(event) => {
              const notes = event.target.value;
              change((current) => ({ ...current, notes }));
            }}
          />
        </HandoverCard>

        <HandoverCard>
          <h2 className="font-heading text-[17px] font-semibold text-text-primary">
            {t('handover.photos')}
          </h2>
          <div className="grid grid-cols-3 gap-2">
            {state.fileIds.map((fileId) => (
              <div key={fileId} className="relative">
                <HandoverPhoto fileId={fileId} />
                {!readOnly ? (
                  <button
                    type="button"
                    aria-label={t('handover.removePhoto')}
                    className="absolute right-1 top-1 rounded-pill bg-surface p-1 shadow-sm"
                    onClick={() =>
                      change((current) => ({
                        ...current,
                        fileIds: current.fileIds.filter((candidate) => candidate !== fileId),
                      }))
                    }
                  >
                    <X aria-hidden className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            ))}
            {state.fileIds.length < HANDOVER_MAX_PHOTOS && !readOnly && online ? (
              <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-border-strong text-center text-small text-text-secondary">
                <Camera aria-hidden className="h-6 w-6" />
                {t('handover.addPhoto')}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="sr-only"
                  data-testid="handover-photo-input"
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    const fileId = await upload.upload(file, {
                      ownerModule: 'shift-handover',
                      purpose: 'handover_photo',
                    });
                    if (fileId)
                      change((current) => ({ ...current, fileIds: [...current.fileIds, fileId] }));
                  }}
                />
              </label>
            ) : null}
          </div>
          {upload.error ? (
            <p role="alert" className="text-caption text-danger">
              {upload.error}
            </p>
          ) : null}
        </HandoverCard>

        {/* Bureau: de samenvatting rechts, zoals de mockup. */}
        <HandoverCard className="hidden wide:flex">
          <h2 className="font-heading text-[17px] font-semibold text-text-primary">
            {t('handover.summary')}
          </h2>
          {summary}
          {indienKnop}
        </HandoverCard>

        <PreviousLink lineId={lineId} />
      </div>

      {/* Telefoon: vaste voet "Controleer en dien in" boven de onderbalk. */}
      <div className="fixed inset-x-0 bottom-16 z-10 border-t border-border bg-surface p-3 wide:hidden">
        <div className="mx-auto max-w-md">{indienKnop}</div>
      </div>

      {/* Frame 3b: het controlevenster. */}
      <Dialog open={controle} onOpenChange={setControle} title={t('handover.checkTitle')}>
        <div className="flex flex-col gap-element" data-testid="handover-check-dialog">
          {summary}
          {server.next ? (
            <p className="text-content text-text-secondary">
              {t('handover.nextGets', {
                slot: server.next.slotName,
                time: clock(server.next.startsAt, i18n.language),
              })}
            </p>
          ) : null}
          <Button
            size="lg"
            loading={save.isPending || submit.isPending}
            onClick={() => void dienIn()}
            data-testid="handover-submit"
          >
            {server.status === 'draft' ? t('handover.submit') : t('handover.saveChanges')}
          </Button>
          <Button size="lg" variant="secondary" onClick={() => setControle(false)}>
            {t('handover.keepEditing')}
          </Button>
        </div>
      </Dialog>
    </div>,
  );
}

/** "Vorige overdracht" (bureau, rechterkolom). */
function PreviousLink({ lineId }: { lineId: string }) {
  const { t, i18n } = useTranslation();
  const { online } = useOffline();
  const history = useHandoverHistory(lineId, online);
  const vorige = history.data?.[0];
  if (!vorige) return null;
  return (
    <HandoverCard className={cn('hidden wide:flex')}>
      <h2 className="font-heading text-[17px] font-semibold text-text-primary">
        {t('handover.previous')}
      </h2>
      <Link
        to="/app/overdracht/$handoverId"
        params={{ handoverId: vorige.id }}
        className="text-content font-semibold text-[var(--brand-primary-aa)]"
      >
        {shortDate(vorige.shiftDate, i18n.language)} · {vorige.slotName}
      </Link>
    </HandoverCard>
  );
}
